/**
 * Writes a comment or reply. Typing "@" suggests the agency's staff (arrow
 * keys and Enter, or a click); a picked name is kept as a mention only while
 * its "@Name" is still in the text.
 */

import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

import type { NewComment, StaffMember } from './comments-api.ts';

const MAX_BODY = 2000;
const MAX_SUGGESTIONS = 6;

/** The "@partial" being typed just before the cursor, if any. */
function mentionQuery(text: string, cursor: number): { start: number; query: string } | null {
  const match = /(^|\s)@([^\s@]{0,30})$/.exec(text.slice(0, cursor));
  if (!match) return null;
  return { start: cursor - (match[2]?.length ?? 0) - 1, query: (match[2] ?? '').toLowerCase() };
}

export function CommentComposer({
  staff,
  placeholder,
  submitLabel,
  onSubmit,
}: {
  staff: StaffMember[];
  placeholder: string;
  submitLabel: string;
  onSubmit: (comment: Pick<NewComment, 'body' | 'mentions'>) => Promise<void>;
}): ReactNode {
  const listId = useId();
  const field = useRef<HTMLTextAreaElement | null>(null);
  const [body, setBody] = useState('');
  const [picked, setPicked] = useState<StaffMember[]>([]);
  const [typing, setTyping] = useState<{ start: number; query: string } | null>(null);
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const suggestions = typing
    ? staff
        .filter(
          (person) =>
            person.name.toLowerCase().includes(typing.query) ||
            person.email.toLowerCase().startsWith(typing.query),
        )
        .slice(0, MAX_SUGGESTIONS)
    : [];

  const pick = (person: StaffMember) => {
    if (!typing || !field.current) return;
    const cursor = field.current.selectionStart;
    const next = `${body.slice(0, typing.start)}@${person.name} ${body.slice(cursor)}`;
    setBody(next);
    setPicked((current) => [...current.filter((item) => item.user_id !== person.user_id), person]);
    setTyping(null);
    field.current.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (suggestions.length === 0) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((current) => (current + step + suggestions.length) % suggestions.length);
    } else if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      const person = suggestions[active];
      if (person) pick(person);
    } else if (event.key === 'Escape') {
      event.stopPropagation();
      setTyping(null);
    }
  };

  const submit = async () => {
    const text = body.trim();
    if (!text) return;
    setBusy(true);
    try {
      const mentions = picked
        .filter((person) => text.includes(`@${person.name}`))
        .map((person) => person.user_id);
      await onSubmit({ body: text, mentions });
      setBody('');
      setPicked([]);
      setProblem(null);
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'The comment could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  const open = suggestions.length > 0;
  return (
    <form
      className="rcm-compose"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <textarea
        ref={field}
        value={body}
        maxLength={MAX_BODY}
        rows={2}
        placeholder={placeholder}
        aria-label={placeholder}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open ? `${listId}-${active}` : undefined}
        onKeyDown={onKeyDown}
        onChange={(event) => {
          setBody(event.target.value);
          setTyping(mentionQuery(event.target.value, event.target.selectionStart));
          setActive(0);
        }}
      />
      {open && (
        <ul id={listId} role="listbox" className="rcm-suggest" aria-label="Mention someone">
          {suggestions.map((person, index) => (
            <li
              key={person.user_id}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === active}
              onMouseDown={(event) => {
                event.preventDefault();
                pick(person);
              }}
            >
              <strong>{person.name}</strong> <small>{person.email}</small>
            </li>
          ))}
        </ul>
      )}
      {problem && (
        <p className="rh-access-msg rh-access-error" role="alert">
          {problem}
        </p>
      )}
      <button type="submit" className="btn small" disabled={busy || !body.trim()}>
        {submitLabel}
      </button>
    </form>
  );
}
